import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import type { SvgIconProps } from '@material-ui/core/SvgIcon'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import classnames from 'classnames'
import React from 'react'
import type { IconButtonProps } from '@material-ui/core/IconButton'
import { default as IconButton } from '@material-ui/core/IconButton'
import type { StyleClasses } from '../../styles'
import PopupMenu from '../PopupMenu'
import MenuItem from '../MenuItem'
import Typography from '../Typography'

export interface ActionButtonProps extends IconButtonProps {
  classes?: StyleClasses<typeof useStyles>
  className?: string
  actions: Action[]
  disabled?: boolean
  button?: React.ReactNode
  onActionClick: (event: React.MouseEvent<HTMLElement>, actionKey: string, actionPayload?: any) => void
}

export interface Action {
  key: string

  /**
   * Text of the MenuItem
   */
  label: string
  sublabel?: string

  scary?: boolean

  payload?: any

  /**
   * Icon for the MenuItem
   */
  icon?: React.ComponentType<SvgIconProps>
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  root: {},
  button: {
    padding: theme.spacing(1)
  },
  menuItem: {
    display: 'flex',
    alignItems: 'flex-start'
  },
  divider: {
    padding: 0,
    height: 0,
    minHeight: 0,
    paddingBottom: theme.spacing(),
    marginBottom: theme.spacing(),
    borderBottom: `1px solid ${theme.palette.grey[300]}`
  },
  scary: {
    color: theme.palette.error.main
  }
}))

function ActionButton({ disabled, actions, className, onActionClick, button, ...props }: ActionButtonProps) {
  const classes = useStyles(props)
  const { classes: _classProps, ...baseIconProps } = props

  return (
    <PopupMenu
      className={classnames(classes.root, className)}
      component={
        React.isValidElement(button) ? (
          button
        ) : (
          <IconButton
            className={classes.button}
            color="inherit"
            disabled={disabled}
            disableRipple={true}
            onClick={(ev) => ev.stopPropagation()}
            // `id` is a default, not an override: every instance of this shared button used to get
            // the literal id "action-button-icon" no matter how many rendered on one page (invalid,
            // duplicate DOM ids), which forced BugBug tests to select it by numeric position instead
            // of a stable identifier. Spreading `baseIconProps` after `id` lets a caller override it
            // (or pass `data-testid`) while callers that pass nothing keep the old id unchanged.
            id="action-button-icon"
            {...baseIconProps}
          >
            <MoreHorizIcon />
          </IconButton>
        )
      }
    >
      {actions &&
        actions.map((action, index) => {
          if (action.key === 'divider') {
            return <MenuItem key={`${action}-${index}`} disabled classes={{ root: classes.divider }} />
          }

          return (
            <MenuItem
              className={classnames({ [classes.menuItem]: true, [classes.scary]: action.scary })}
              key={action.key}
              icon={action.icon}
              onClick={(ev) => {
                onActionClick(ev, action.key, action.payload)
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                {action.label}
                {action.sublabel && (
                  <Typography variant="caption" color="textSecondary">
                    {action.sublabel}
                  </Typography>
                )}
              </div>
            </MenuItem>
          )
        })}
    </PopupMenu>
  )
}

export default ActionButton
